import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { fmt } from "@/lib/format";

const LIMIT = 50;

export default function AdminTokens() {
  const [data, setData] = useState({ total: 0, items: [], skip: 0 });
  const [filters, setFilters] = useState({ status: "all", property_id: "all", q: "" });
  const [props, setProps] = useState([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(LIMIT), skip: String(skip) });
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.property_id !== "all") params.set("property_id", filters.property_id);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      try {
        const { data } = await api.get(`/admin/tokens?${params}`);
        setData(data);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [filters, skip]);

  const onFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setSkip(0);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / LIMIT));
  const currentPage = Math.floor(skip / LIMIT) + 1;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Ledger</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-2">
        Token registry
      </h1>
      <p className="text-gray-400 mb-8 max-w-2xl">
        Every brick across every property is a unique on-ledger token. Browse, search and inspect ownership records.
      </p>

      {/* Filters */}
      <div className="bp-surface rounded-2xl p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="overline text-[10px] mb-1">Token ID search</div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <Input
              data-testid="tokens-search"
              placeholder="BP-XXXXXXXX-00001"
              value={filters.q}
              onChange={(e) => onFilter("q", e.target.value)}
              className="pl-9 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37] font-mono"
            />
          </div>
        </div>
        <div className="w-44">
          <div className="overline text-[10px] mb-1">Status</div>
          <Select value={filters.status} onValueChange={(v) => onFilter("status", v)}>
            <SelectTrigger data-testid="tokens-filter-status" className="bg-[#050914] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0B1120] text-white border-white/10">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-60">
          <div className="overline text-[10px] mb-1">Property</div>
          <Select value={filters.property_id} onValueChange={(v) => onFilter("property_id", v)}>
            <SelectTrigger data-testid="tokens-filter-property" className="bg-[#050914] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0B1120] text-white border-white/10 max-h-72">
              <SelectItem value="all">All properties</SelectItem>
              {props.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-gray-500">
          <div className="overline text-[10px] mb-1">Total</div>
          <div className="font-mono text-white text-base" data-testid="tokens-total">{data.total.toLocaleString()}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bp-surface rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="overline">Token ID</TableHead>
              <TableHead className="overline">Property</TableHead>
              <TableHead className="overline">Brick #</TableHead>
              <TableHead className="overline">Status</TableHead>
              <TableHead className="overline">Owner</TableHead>
              <TableHead className="overline">Price paid</TableHead>
              <TableHead className="overline">Resale</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-gray-500">Loading...</TableCell>
              </TableRow>
            ) : data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-gray-500">No tokens match filters</TableCell>
              </TableRow>
            ) : (
              data.items.map((b) => (
                <TableRow key={b.id} className="border-white/5" data-testid={`token-${b.id}`}>
                  <TableCell className="font-mono text-[#D4AF37] text-xs">{b.token_id}</TableCell>
                  <TableCell>
                    <div className="text-white text-sm">{b.property_name}</div>
                    <div className="text-[11px] text-gray-500">{b.property_location}</div>
                  </TableCell>
                  <TableCell className="font-mono text-white">{String(b.brick_number).padStart(5, "0")}</TableCell>
                  <TableCell><StatusPill status={b.status} /></TableCell>
                  <TableCell>
                    {b.owner_name ? (
                      <>
                        <div className="text-white text-sm">{b.owner_name}</div>
                        <div className="text-[11px] text-gray-500">{b.owner_email}</div>
                      </>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-gray-300">
                    {b.purchase_price ? fmt(b.purchase_price, { decimals: true }) : "—"}
                  </TableCell>
                  <TableCell>
                    {b.listed_for_resale ? (
                      <span className="font-mono text-blue-400 text-sm">{fmt(b.resale_price, { decimals: true })}</span>
                    ) : (
                      <span className="text-gray-600 text-xs">Not listed</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Showing {skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              data-testid="tokens-prev"
            >
              <ChevronLeft className="w-3 h-3 mr-1" /> Prev
            </Button>
            <div className="px-3 py-1 text-xs text-gray-400 font-mono self-center">
              {currentPage} / {totalPages}
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={skip + LIMIT >= data.total}
              onClick={() => setSkip(skip + LIMIT)}
              data-testid="tokens-next"
            >
              Next <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    available: { c: "#10B981", label: "Available" },
    sold: { c: "#D4AF37", label: "Sold" },
  };
  const s = map[status] || { c: "#6B7280", label: status };
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ color: s.c, background: `${s.c}15`, border: `1px solid ${s.c}40` }}
    >
      {s.label}
    </span>
  );
}
